import type { DistributionCountries } from 'models/DistributionCountry';
import { ProductVariationBase } from 'models/productsReferential';
import type { Underlying } from 'models/underlying';
import { createSelector } from 'reselect';
import type { AppState } from 'state/state.model';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import { addKeys } from 'state/utils/collection';
import { distinct } from 'utils/array/array';

import type { DataById, FormDataState, FormDataStateMap, FormFieldId } from './formData.model';

type DataType = 'enriched' | 'input' | 'pendingEnrichment';

const emptyData = {};

function getData(state: FormDataState, formId: string, dataType: DataType): DataById {
  return state?.[dataType]?.[formId] || emptyData;
}

export function getFormEnrichedData(state: AppState, formId: string) {
  return getData(state.formData, formId, 'enriched');
}

export function selectFormEnrichedSolvingMode(state: AppState, formId: string) {
  return getData(state.formData, formId, 'enriched')['SolvingMode'] as string;
}

export function getFormEnrichedIsBackToBack(state: AppState, formId: string): boolean {
  return getData(state.formData, formId, 'enriched')['BackToBackIsBackToBack'] as boolean;
}
export function getFormEnrichedBackToBackFileCode(state: AppState, formId: string): string | undefined {
  return getData(state.formData, formId, 'enriched')['BackToBackFileCode'] as string;
}

export function getFormEnrichedBackToBackType(state: AppState, formId: string): BackToBackType | undefined {
  return getData(state.formData, formId, 'enriched')['BackToBackType'] as BackToBackType;
}

export function getFormEnrichedBackedNotional(state: AppState, formId: string): number | undefined {
  return getData(state.formData, formId, 'enriched')['BackedNotional'] as number;
}

export function getFormEnrichedNoteNominal(state: AppState, formId: string): number | undefined {
  return getData(state.formData, formId, 'enriched')['NoteNominal'] as number;
}

export function getFormEnrichedIsDeltaAdjusted(state: AppState, formId: string): boolean | undefined {
  const value = getData(state.formData, formId, 'enriched')['IsDeltaAdjusted'];
  return typeof value === 'boolean' ? value : undefined;
}

export function getFormEnrichedIsDelayedBackToBack(state: AppState, formId: string): boolean | undefined {
  const value = getData(state.formData, formId, 'enriched')['IsDelayedBackToBack'];
  return typeof value === 'boolean' ? value : undefined;
}

export function getFormEnrichedProductSubType(state: AppState, formId: string): string {
  const data = getFormEnrichedData(state, formId);
  return data['ProductSubtype'] as string;
}

export function getFormEnrichedProductType(state: AppState, formId: string): string {
  const data = getFormEnrichedData(state, formId);
  return data['ProductType'] as string;
}

export function isOtcProduct(state: AppState, formId: string): boolean {
  const data = getFormEnrichedData(state, formId);
  const wrapper = data['Wrapper'] as string;
  return wrapper === 'Option' || wrapper === 'Swap';
}

export function getFormEnrichedDistributionCountries(state: AppState, tabId: string): string[] {
  const distributionCountry = getFormEnrichedData(state, tabId).DistributionCountry as DistributionCountries;
  return distributionCountry?.map(distributionCountry => distributionCountry.DistributionCountryCountry) ?? [];
}

export function getFormEnrichedUnderlyings(state: AppState, formId: string): Underlying[] {
  const data = getFormEnrichedData(state, formId);
  return (data['Underlyings'] as Underlying[]) ?? [];
}
export function getFormEnrichedTradeDate(state: AppState, formId: string): string | undefined {
  const data = getFormEnrichedData(state, formId);
  return data['TradeDate'] as string | undefined;
}
export function getFormEnrichedUnderlying(state: AppState, formId: string): Underlying | undefined {
  const data = getFormEnrichedData(state, formId);
  return data['Underlying'] as Underlying | undefined;
}

export function getFormsEnrichedProductVariations(state: AppState, formIds: readonly string[]): ProductVariationBase[] {
  const firstFormProductSubType = getFormEnrichedProductSubType(state, formIds[0]);
  const firstFormProductType = getFormEnrichedProductType(state, formIds[0]);
  const productVariations = formIds
    .map(formId => {
      return {
        productSubType: getFormEnrichedProductSubType(state, formId),
        productType: getFormEnrichedProductType(state, formId),
      };
    })
    .filter(distinct)
    .filter(value => value.productSubType !== firstFormProductSubType && value.productType !== firstFormProductType)
    .sort();
  productVariations.unshift({ productSubType: firstFormProductSubType, productType: firstFormProductType });
  return productVariations;
}

export function getFormsEnrichedData(state: AppState, formIds: readonly string[]): FormDataStateMap {
  const data = formIds.map(id => getFormEnrichedData(state, id));
  return addKeys({}, formIds, data);
}

export function getFormInputData(state: AppState, formId: string) {
  return getData(state.formData, formId, 'input');
}

export function getFormPendingEnrichmentData(state: AppState, formId: string) {
  return getData(state.formData, formId, 'pendingEnrichment');
}

export const getCombinedFormData = createSelector(
  [getFormInputData, getFormPendingEnrichmentData, getFormEnrichedData],
  (inputData, pendingEnrichmentData, enrichedData) => ({ ...enrichedData, ...pendingEnrichmentData, ...inputData }),
);
export function isPendingEnrichment(state: AppState, formId: string): boolean {
  return Object.keys(getFormPendingEnrichmentData(state, formId)).length > 0;
}

export function isFormInputData(state: AppState, formId: string): boolean {
  return Object.keys(getFormInputData(state, formId)).length > 0;
}

export function getFormDataSnapshot(state: AppState, formId: string, patch?: DataById): DataById {
  const enrichedAndInputData = { ...getFormEnrichedData(state, formId), ...getFormInputData(state, formId) };
  return patch ? { ...enrichedAndInputData, ...patch } : enrichedAndInputData;
}

export function selectEnrichedFieldIdValue(state: AppState, formId: string, fieldId: string | FormFieldId) {
  return state.formData.enriched?.[formId]?.[fieldId];
}

export function selectPendingEnrichedFieldIdValue(state: AppState, formId: string, fieldId: string | FormFieldId) {
  return state.formData.pendingEnrichment?.[formId]?.[fieldId];
}

export function selectInputFieldIdValue(state: AppState, formId: string, fieldId: string | FormFieldId) {
  return state.formData.input?.[formId]?.[fieldId];
}

export const selectFieldIdValue = createSelector(
  [selectInputFieldIdValue, selectPendingEnrichedFieldIdValue, selectEnrichedFieldIdValue],
  (inputValue, pendingEnrichmentValue, enrichedValue) => {
    return inputValue ?? pendingEnrichmentValue ?? enrichedValue;
  },
);

export function selectIsFormDataPendingValue(state: AppState, tabId: string) {
  return state.formData.pendingEnrichment?.[tabId] !== undefined;
}

export function selectEnrichedCombinedDocTitle(state: AppState, tabId: string) {
  const title = (state.formData.enriched?.[tabId]?.DocumentationTitle as string) || undefined;
  const underlyingTitle = (state.formData.enriched?.[tabId]?.DocumentationTitleUnderlyings as string) || undefined;

  if (title && underlyingTitle) {
    return `${title}-${underlyingTitle}`;
  }

  if (title) {
    return title;
  }

  return underlyingTitle;
}
